import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { HubScreen } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import s from "../clinic.module.css";
import { Skeleton, useToast } from "../../ui";
import { ActTarget } from "../../../activation";
import {
  filterDashboardNotifications,
  mergeAdminNotificationFeed,
  normalizeAdminNotification,
} from "../../../utils/notificationDisplay";

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function NotifyRow({ item }) {
  const href = item.url || item.action_path || item.href;
  const inner = (
    <>
      {item.title ? (
        <span className={aa.adminNotifyRowLabel}>{item.title}</span>
      ) : null}
      <span className={s.summaryNotifyText}>{item.text}</span>
      {href ? <span className={s.summaryNotifyAction}>Открыть →</span> : null}
      {item.sent_at ? (
        <time className={aa.adminNotifyRowTime}>{formatWhen(item.sent_at)}</time>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className={`${s.summaryNotifyItem} ${s.summaryNotifyItemSwipe} ${
          s[`summaryNotify_${item.tone}`] || ""
        } ${aa.adminNotifyRow}`}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={`${s.summaryNotifyItem} ${s.summaryNotifyItemSwipe} ${
        s[`summaryNotify_${item.tone}`] || ""
      } ${aa.adminNotifyRow}`}
    >
      {inner}
    </div>
  );
}

export default function AdminNotifications({ hubBase = "/manage/app" }) {
  const toast = useToast();
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pushRes, dash] = await Promise.all([
        clinicAPI.getMyAdminNotifications(),
        clinicAPI.getSportToday(),
      ]);
      const pushItems = (pushRes.notifications || []).map(normalizeAdminNotification);
      const dashItems = filterDashboardNotifications(dash?.notifications).map(
        (n) =>
          normalizeAdminNotification({
            ...n,
            title: n.action_label || "Задача",
            url: n.action_path,
            sent_at: new Date().toISOString(),
          }),
      );
      setItems(mergeAdminNotificationFeed(pushItems, dashItems));
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const tasks = items.filter((i) => i.source === "dashboard" || i.action_path);
    const feed = items.filter((i) => !tasks.includes(i));
    return { tasks, feed: tasks.length ? [...tasks, ...feed] : items };
  }, [items]);

  const listBody = loading ? (
    <Skeleton height={120} style={{ borderRadius: 16 }} />
  ) : grouped.feed.length === 0 ? (
    <div className={aa.adminNotifyEmpty}>
      <span aria-hidden>✓</span>
      <p>Нет уведомлений — здесь появятся push-сообщения и задачи клуба</p>
    </div>
  ) : (
    <ActTarget stepId="notifications" subId="notifications_list" as="div">
      <ul className={aa.adminNotifyList}>
        {grouped.feed.map((n) => (
          <li key={n.id}>
            <NotifyRow item={n} />
          </li>
        ))}
      </ul>
    </ActTarget>
  );

  if (!isMobile) {
    return (
      <div className={`${md.desktopHubPage} ${md.desktopNotifyPage}`}>
        <header className={md.desktopHubPageHead}>
          <ActTarget stepId="notifications" subId="notifications_tasks" as="div">
          <div>
            <h1 className={md.desktopHubPageTitle}>Уведомления</h1>
            <p className={md.desktopHubPageLead}>
              Push-сообщения и задачи, которые требуют внимания администратора.
            </p>
          </div>
          </ActTarget>
        </header>
        <div className={md.desktopHubPanel}>{listBody}</div>
      </div>
    );
  }

  return (
    <HubScreen
      title="Уведомления"
      onBack={() => navigate(hubBase)}
      bodyClassName={aa.adminNotifyBody}
    >
      {listBody}
    </HubScreen>
  );
}
